import { useCallback, useState } from "react";

import { analyticsService } from "../lib/analytics";
import { reviewService } from "../lib/reviews";

type UseFeedbackDialogOptions = {
  initialOpen?: boolean;
  initialRating?: number;
  initialSubmitted?: boolean;
};

export function useFeedbackDialog({
  initialOpen = false,
  initialRating,
  initialSubmitted = false,
}: UseFeedbackDialogOptions = {}) {
  const [open, setOpen] = useState(initialOpen);
  const [rating, setRating] = useState<number | undefined>(initialRating);
  const [submitted, setSubmitted] = useState(initialSubmitted);

  // Handler for star rating feedback (case-level)
  const handlePress = useCallback(
    (newRating: number) => {
      // Don't open dialog if already submitted
      if (submitted) {
        return;
      }

      analyticsService.trackEvent("FeedbackProvided", { rating: newRating });

      setRating(newRating);
      setOpen(true);
    },
    [submitted],
  );

  const handleReviewRequest = useCallback(async () => {
    const isAvailable = await reviewService.isAvailable();
    if (isAvailable) {
      void reviewService.requestReview();
    }
  }, []);

  const onFeedbackSubmitted = useCallback(() => {
    setSubmitted(true);

    // If rating is 4 or above, attempt to show the store review dialog
    if (rating && rating >= 4) {
      void handleReviewRequest();
    }
  }, [rating, handleReviewRequest]);

  return {
    handlePress,

    // Destined for FeedbackDialog
    dialogProps: {
      open,
      onOpenChange: setOpen,
      rating,
      onRatingChange: setRating,
      submitted: submitted,
      onFeedbackSubmitted,
    },
  };
}
